import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const FileUploadSection = ({ data, onChange, errors }) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const allowedFileTypes = {
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-powerpoint': '.ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'text/plain': '.txt',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx'
  };

  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(Array.from(e?.dataTransfer?.files));
    }
  };

  const handleFileSelect = (e) => {
    if (e?.target?.files) {
      handleFiles(Array.from(e?.target?.files));
    }
  };

  const handleFiles = (files) => {
    const validFiles = [];
    const errors = [];

    files?.forEach(file => {
      // Check file type
      if (!Object.keys(allowedFileTypes)?.includes(file?.type)) {
        errors?.push(`${file?.name}: File type not supported`);
        return;
      }

      // Check file size
      if (file?.size > maxFileSize) {
        errors?.push(`${file?.name}: File size exceeds 10MB limit`);
        return;
      }

      // Create file object with preview
      const fileObj = {
        id: Date.now() + Math.random(),
        file: file,
        name: file?.name,
        size: file?.size,
        type: file?.type,
        uploadProgress: 0,
        uploaded: false
      };

      validFiles?.push(fileObj);
    });

    if (validFiles?.length > 0) {
      const currentFiles = data?.attachments || [];
      onChange('attachments', [...currentFiles, ...validFiles]);
      
      // Simulate upload progress
      validFiles?.forEach(fileObj => {
        simulateUpload(fileObj?.id);
      });
    }

    if (errors?.length > 0) {
      console.error('File upload errors:', errors);
    }
  };

  const simulateUpload = (fileId) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        updateFileProgress(fileId, 100, true);
      } else {
        updateFileProgress(fileId, progress, false);
      }
    }, 200);
  };

  const updateFileProgress = (fileId, progress, uploaded) => {
    const currentFiles = data?.attachments || [];
    const updatedFiles = currentFiles?.map(file => 
      file?.id === fileId 
        ? { ...file, uploadProgress: progress, uploaded: uploaded }
        : file
    );
    onChange('attachments', updatedFiles);
  };

  const removeFile = (fileId) => {
    const currentFiles = data?.attachments || [];
    onChange('attachments', currentFiles?.filter(file => file?.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return 'FileText';
    if (fileType?.includes('word')) return 'FileText';
    if (fileType?.includes('powerpoint') || fileType?.includes('presentation')) return 'Presentation';
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return 'Table';
    if (fileType?.includes('image')) return 'Image';
    return 'File';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
          <Icon name="Upload" size={16} color="white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Supporting Documents</h2>
          <p className="text-sm text-muted-foreground">Upload relevant files and reference materials</p>
        </div>
      </div>
      {/* Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center transition-micro
          ${dragActive 
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
          }
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={Object.values(allowedFileTypes)?.join(',')}
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        
        <div className="space-y-4">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
            <Icon name="Upload" size={24} className="text-muted-foreground" />
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              Drop files here or click to browse
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Support for PDF, DOC, PPT, images, and more. Max file size: 10MB
            </p>
            
            <Button
              variant="outline"
              onClick={() => fileInputRef?.current?.click()}
              iconName="Plus"
              iconPosition="left"
              iconSize={16}
            >
              Select Files
            </Button>
          </div>
        </div>
      </div>
      {/* File Categories */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="FileText" size={16} className="text-primary" />
            <h4 className="font-medium text-foreground">Problem Documentation</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            Detailed problem descriptions, research reports, data analysis
          </p>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Image" size={16} className="text-primary" />
            <h4 className="font-medium text-foreground">Visual References</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            Photos, diagrams, maps, charts, mockups, or visual examples
          </p>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Database" size={16} className="text-primary" />
            <h4 className="font-medium text-foreground">Data & Resources</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            Datasets, API documentation, technical specifications
          </p>
        </div>
      </div>
      {/* Uploaded Files List */}
      {data?.attachments && data?.attachments?.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-medium text-foreground">Uploaded Files</h3>
          
          <div className="space-y-2">
            {data?.attachments?.map((file) => (
              <div key={file?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                      <Icon name={getFileIcon(file?.type)} size={16} className="text-muted-foreground" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{file?.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(file?.size)}
                        {file?.uploaded && (
                          <span className="ml-2 text-success">✓ Uploaded</span>
                        )}
                      </p>
                      
                      {!file?.uploaded && (
                        <div className="mt-2">
                          <div className="w-full bg-muted rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full transition-layout"
                              style={{ width: `${file?.uploadProgress}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Uploading... {Math.round(file?.uploadProgress)}%
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFile(file?.id)}
                    iconName="Trash2"
                    iconSize={16}
                    className="text-muted-foreground hover:text-error"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Upload Guidelines */}
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-accent mt-0.5" />
          <div>
            <h4 className="font-medium text-foreground mb-2">File Upload Guidelines</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Include any existing research, studies, or documentation</li>
              <li>• Add visual references like photos of the problem area</li>
              <li>• Provide data files or API documentation if available</li>
              <li>• Upload technical specifications or requirements documents</li>
              <li>• All files are securely stored and only shared with assigned teams</li>
            </ul>
          </div>
        </div>
      </div>
      {errors?.attachments && (
        <p className="text-sm text-error">{errors?.attachments}</p>
      )}
    </div>
  );
};

export default FileUploadSection;